import React, { useState, useCallback } from 'react';
import styled from '@emotion/styled';

import ApplicationIntake from '~/components/Applications/ApplicationIntake';
import CalendarInput from '~/components/CalendarInput';
import useInitialProps from '~/hooks/useInitialProps';
import Modal, {
  Header as ModalHeader,
  Content as ModalContent,
  Buttons as ModalButtons,
} from './Modal';
import { useProgramIntakes } from '~/data/programIntakes';

const InputGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gridColumnGap: '20px',
});

type StartDatesPayloadType = {
  intake_date?: string;
  esl_intake_date?: string;
  esl_actual_start_date?: string;
  esl_alternate_actual_start_date?: string;
  program_actual_start_date?: string;
  program_alternate_actual_start_date?: string;
};

type ActualStartDatesModalPropType = {
  application?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  visible?: boolean;
  onSubmit: (a: StartDatesPayloadType) => void;
  onClose: (a: any) => any; // eslint-disable-line @typescript-eslint/no-explicit-any
  errors?: { [key: string]: string };
  intake: string;
  eslIntake: string;
};

const ActualStartDatesModal = ({
  onSubmit,
  onClose,
  application,
  errors = {},
}: ActualStartDatesModalPropType) => {
  const initialProps = useInitialProps();
  const [formState, setFormState] = useState<StartDatesPayloadType>({
    intake_date: application.intake,
    esl_intake_date: application.intake_esl,
  });

  const onChange = useCallback(
    (key: string, value: string) =>
      setFormState(currentState => ({
        ...currentState,
        [key]: value,
      })),
    [setFormState],
  );

  const onChangeEslValue = (id: string, value: string) => {
    setFormState(currentState => ({
      ...currentState,
      esl_intake_id: id,
      esl_intake_date: value,
    }));
  };

  const onChangeAcademicValue = (id: string, value: string) => {
    setFormState(currentState => ({
      ...currentState,
      main_intake_id: id,
      intake_date: value,
    }));
  };

  const handleSubmit = useCallback(() => {
    onSubmit(formState);
  }, [onSubmit, formState]);

  const fakeApplication = {
    ...application,
    intake: formState.intake_date,
    intake_esl: formState.esl_intake_date,
  };
  const fakeStudent = {
    has_visas: initialProps.has_visas,
    nationality_code: initialProps.nationality_code,
  };

  const defaultProgramIntakeQuery = {
    hasVisas: fakeStudent.has_visas,
    nationality: fakeStudent.nationality_code,
    ignoreAcceptingApps: application?.paid,
  };
  const { programIntakes } = useProgramIntakes({
    programId: application?.program.id,
    enabled: !!application,
    ...defaultProgramIntakeQuery,
  });
  const { programIntakes: programIntakesESL } = useProgramIntakes({
    id: application?.program_esl?.id,
    programId: application?.program_esl?.id,
    enabled: !!application?.program_esl,
    ...defaultProgramIntakeQuery,
  });

  return (
    <Modal labelId="statusModalHeader">
      <ModalHeader id="statusModalHeader" onClose={onClose}>
        Status Update Confirmation
      </ModalHeader>
      <ModalContent>
        <div>Please select start date(s) to update application to &lsquo;Ready for Visa&lsquo;</div>
        <InputGrid>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label style={{ lineHeight: '18px', marginTop: '6px' }}>
            ESL Intake
            <ApplicationIntake
              intakes={programIntakes}
              intakesESL={programIntakesESL}
              application={fakeApplication}
              student={fakeStudent}
              showAvailability={true}
              readOnly={false}
              esl={true}
              allowEslEdit={true}
              onChange={onChangeEslValue}
              shouldShowIntakeStatus={true}
            />
          </label>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label style={{ lineHeight: '18px', marginTop: '6px' }}>
            ACD Intake
            <ApplicationIntake
              intakes={programIntakes}
              intakesESL={programIntakesESL}
              application={fakeApplication}
              student={fakeStudent}
              showAvailability={true}
              readOnly={false}
              esl={false}
              allowEslEdit={true}
              onChange={onChangeAcademicValue}
              shouldShowIntakeStatus={true}
            />
          </label>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label>
            ESL Start Date
            <CalendarInput
              value={formState.esl_actual_start_date || ''}
              error={errors.esl_actual_start_date}
              name="english_sl_actual_start_date" // Temporary change to make sure all autocomplete change get reset 2021-10-07
              onChange={e => onChange('esl_actual_start_date', e.target.value)}
            />
          </label>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label>
            ACD Start Date
            <CalendarInput
              value={formState.program_actual_start_date || ''}
              error={errors.program_actual_start_date}
              name="academic_actual_start_date"
              onChange={e => onChange('program_actual_start_date', e.target.value)}
            />
          </label>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label>
            Alternative ESL Start Date
            <CalendarInput
              value={formState.esl_alternate_actual_start_date || ''}
              error={errors.esl_alternate_actual_start_date}
              name="english_sl_alternate_actual_start_date"
              onChange={e => onChange('esl_alternate_actual_start_date', e.target.value)}
            />
          </label>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label>
            Alternative ACD Start Date
            <CalendarInput
              value={formState.program_alternate_actual_start_date || ''}
              error={errors.program_alternate_actual_start_date}
              name="academic_alternate_actual_start_date"
              onChange={e => onChange('program_alternate_actual_start_date', e.target.value)}
            />
          </label>
        </InputGrid>
      </ModalContent>
      <ModalButtons>
        <button onClick={onClose}>Cancel</button>
        <button className="default" onClick={handleSubmit}>
          Confirm
        </button>
      </ModalButtons>
    </Modal>
  );
};

export default ActualStartDatesModal;
